import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle } from "lucide-react";
import { PHONES } from "../i18n";

const WA_NUMBER = PHONES[1]?.tel?.replace(/\+/g, "") || "34676423872"; // mobile
const MESSAGE_ES = "Hola Q5, quería preguntaros sobre...";
const MESSAGE_EN = "Hi Q5, I wanted to ask about...";

/**
 * Floating WhatsApp CTA. Sits bottom-right on every public page, with a small
 * "¿Necesitas ayuda?" tooltip that pops after a delay on the first visit to
 * nudge the user without being annoying.
 */
export const WhatsAppFab = ({ lang = "es" }) => {
  const [showTip, setShowTip] = useState(false);
  const msg = lang === "es" ? MESSAGE_ES : MESSAGE_EN;
  const href = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;

  useEffect(() => {
    // First-visit teaser: pop the tooltip after ~3.5s once per browser.
    if (sessionStorage.getItem("q5_wa_tip_seen")) return;
    const showId = setTimeout(() => {
      setShowTip(true);
      sessionStorage.setItem("q5_wa_tip_seen", "1");
      // Auto-hide after 7s so it doesn't linger over content.
      const hideId = setTimeout(() => setShowTip(false), 7000);
      return () => clearTimeout(hideId);
    }, 3500);
    return () => clearTimeout(showId);
  }, []);

  return (
    <div data-testid="whatsapp-fab" className="fixed bottom-5 right-5 z-40 flex items-end gap-2 pointer-events-none">
      <AnimatePresence>
        {showTip && (
          <motion.div
            key="tip"
            initial={{ opacity: 0, scale: 0.9, x: 12 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.9, x: 12 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="whatsapp-fab-tip"
            className="pointer-events-auto mb-1 relative max-w-[220px] bg-[#141414] border border-gold/60 shadow-[0_10px_30px_rgba(0,0,0,0.6)] text-white text-[12px] leading-snug px-4 py-3 rounded-sm"
          >
            <button
              onClick={() => setShowTip(false)}
              aria-label="close"
              data-testid="whatsapp-fab-tip-close"
              className="absolute -top-2 -right-2 w-6 h-6 border border-q5 bg-[#0A0A0A] text-neutral-300 hover:text-gold flex items-center justify-center"
            >
              <X size={12} />
            </button>
            <p className="font-serif-q5 text-base text-gold leading-tight mb-1">
              {lang === "es" ? "¿Tienes dudas?" : "Any questions?"}
            </p>
            <p className="text-neutral-300">
              {lang === "es"
                ? "Escríbenos por WhatsApp y te respondemos al momento."
                : "Message us on WhatsApp — we'll reply straight away."}
            </p>
            <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-[#141414] border-r border-b border-gold/60 rotate-45"></span>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp"
        data-testid="whatsapp-fab-btn"
        initial={{ scale: 0, rotate: -90 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.8, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setShowTip(false)}
        className="pointer-events-auto group relative w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#22c85f] shadow-[0_10px_30px_rgba(37,211,102,0.35)] flex items-center justify-center transition-colors"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-70 animate-ping" style={{ animationDuration: "2.6s" }}></span>
        {/* WhatsApp glyph — inline SVG (lucide has MessageCircle but not the exact WA mark) */}
        <svg
          className="relative w-7 h-7 text-white"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M20.52 3.48A11.79 11.79 0 0 0 12.06 0C5.5 0 .17 5.33.17 11.9c0 2.1.55 4.14 1.6 5.94L0 24l6.34-1.66a11.86 11.86 0 0 0 5.72 1.46h.01c6.56 0 11.9-5.33 11.9-11.9a11.79 11.79 0 0 0-3.45-8.42zm-8.46 18.31h-.01a9.89 9.89 0 0 1-5.04-1.38l-.36-.21-3.76.98 1-3.66-.24-.38a9.86 9.86 0 0 1-1.52-5.26c0-5.47 4.45-9.91 9.93-9.91 2.66 0 5.15 1.03 7.03 2.9a9.83 9.83 0 0 1 2.91 7.02c0 5.47-4.45 9.91-9.94 9.91zm5.44-7.42c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07a8.15 8.15 0 0 1-2.4-1.48 9.02 9.02 0 0 1-1.66-2.07c-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.87 1.22 3.07.15.2 2.1 3.2 5.08 4.48.71.31 1.26.49 1.69.63.71.22 1.35.19 1.86.11.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
        </svg>
      </motion.a>
    </div>
  );
};
